import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface MatchDebug {
  sku_raw: string | null;
  sku_key: string | null;
  barcode_raw: string | null;
  barcode_key: string | null;
  barcode_digits_key: string | null;
  matched_via: 'sku' | 'barcode' | 'barcode_digits' | null;
  voorraad_sku_count: number;
  voorraad_barcode_count: number;
  voorraad_digits_count: number;
  reason: string;
}

export interface ExpiredProduct {
  id: string;
  lightspeed_product_id: string;
  sku: string | null;
  barcode: string | null;
  title: string;
  supplier_title: string | null;
  issue_type: 'no_match' | 'locatie_nv' | 'locatie_vervallen' | 'naam_zzz';
  matched_product_id: string | null;
  matched_locatie: string | null;
  matched_product_naam: string | null;
  checked_at: string;
  created_at: string;
  match_debug: MatchDebug | null;
  // Alternative product fields
  alternative_product_naam: string | null;
  alternative_locatie: string | null;
  alternative_voorraad: number | null;
  alternative_match_type: 'zzz_stripped' | 'known_replacement' | 'brand_similar' | 'color_code' | null;
  alternative_sku: string | null;
  alternative_eenheid: string | null;
}

export interface ExpiredProductsStats {
  total: number;
  no_match: number;
  locatie_nv: number;
  locatie_vervallen: number;
  naam_zzz: number;
  with_alternative: number;
  last_check: string | null;
}

export const useExpiredProducts = (issueType?: string) => {
  return useQuery({
    queryKey: ["expired-products", issueType],
    queryFn: async () => {
      let query = supabase
        .from("expired_products")
        .select("*")
        .order("checked_at", { ascending: false })
        .limit(2000);

      if (issueType && issueType !== "all") {
        query = query.eq("issue_type", issueType);
      }

      const { data, error } = await query;

      if (error) {
        console.error("[useExpiredProducts] Error:", error);
        throw error;
      }

      console.log(`[useExpiredProducts] Fetched ${data?.length || 0} products for filter: ${issueType}`);
      return data as ExpiredProduct[];
    },
    staleTime: 1 * 60 * 1000,
  });
};

// Stats hook that follows the current filter
export const useExpiredProductsStats = (issueFilter?: string) => {
  return useQuery({
    queryKey: ["expired-products-stats", issueFilter],
    queryFn: async () => {
      // If filtering by a specific issue type, only count that type
      if (issueFilter && issueFilter !== "all") {
        const [filteredResult, lastCheckResult] = await Promise.all([
          supabase.from("expired_products").select("*", { count: "exact", head: true }).eq("issue_type", issueFilter),
          supabase.from("expired_products").select("checked_at").eq("issue_type", issueFilter).order("checked_at", { ascending: false }).limit(1),
        ]);

        if (filteredResult.error) {
          console.error("[useExpiredProductsStats] Error:", filteredResult.error);
          throw filteredResult.error;
        }

        const count = filteredResult.count || 0;
        
        // Count alternatives within the filtered set
        const altResult = await supabase
          .from("expired_products")
          .select("*", { count: "exact", head: true })
          .eq("issue_type", issueFilter)
          .not("alternative_product_naam", "is", null);
        
        const stats: ExpiredProductsStats = {
          total: count,
          no_match: issueFilter === "no_match" ? count : 0,
          locatie_nv: issueFilter === "locatie_nv" ? count : 0,
          locatie_vervallen: issueFilter === "locatie_vervallen" ? count : 0,
          naam_zzz: issueFilter === "naam_zzz" ? count : 0,
          with_alternative: altResult.count || 0,
          last_check: lastCheckResult.data?.[0]?.checked_at || null,
        };

        console.log("[useExpiredProductsStats] Filtered stats:", stats);
        return stats;
      }

      // No filter or "all" - get all counts
      const [totalResult, noMatchResult, locatieNvResult, locatieVervallenResult, naamZzzResult, withAlternativeResult, lastCheckResult] = await Promise.all([
        supabase.from("expired_products").select("*", { count: "exact", head: true }),
        supabase.from("expired_products").select("*", { count: "exact", head: true }).eq("issue_type", "no_match"),
        supabase.from("expired_products").select("*", { count: "exact", head: true }).eq("issue_type", "locatie_nv"),
        supabase.from("expired_products").select("*", { count: "exact", head: true }).eq("issue_type", "locatie_vervallen"),
        supabase.from("expired_products").select("*", { count: "exact", head: true }).eq("issue_type", "naam_zzz"),
        supabase.from("expired_products").select("*", { count: "exact", head: true }).not("alternative_product_naam", "is", null),
        supabase.from("expired_products").select("checked_at").order("checked_at", { ascending: false }).limit(1),
      ]);

      if (totalResult.error) {
        console.error("[useExpiredProductsStats] Error:", totalResult.error);
        throw totalResult.error;
      }

      const stats: ExpiredProductsStats = {
        total: totalResult.count || 0,
        no_match: noMatchResult.count || 0,
        locatie_nv: locatieNvResult.count || 0,
        locatie_vervallen: locatieVervallenResult.count || 0,
        naam_zzz: naamZzzResult.count || 0,
        with_alternative: withAlternativeResult.count || 0,
        last_check: lastCheckResult.data?.[0]?.checked_at || null,
      };

      console.log("[useExpiredProductsStats] Stats:", stats);
      return stats;
    },
    staleTime: 1 * 60 * 1000,
  });
};

export const useRunExpiredProductsCheck = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke("check-expired-products");

      if (error) {
        throw error;
      }

      return data;
    },
    onSuccess: () => {
      // Force refetch of all matching queries, not just invalidate
      queryClient.invalidateQueries({ 
        queryKey: ["expired-products"],
        refetchType: 'all'
      });
      queryClient.invalidateQueries({ 
        queryKey: ["expired-products-stats"],
        refetchType: 'all'
      });
      queryClient.invalidateQueries({ queryKey: ["workflow-executions"] });
    },
  });
};
